import { AbilityBuilder, Ability } from "@casl/ability";

export type Actions =
  | "create"
  | "read"
  | "update"
  | "delete"
  | "getAll"
  | "isSuperAdmin";

export type Modules =
  | "Planner"
  | "Routine"
  | "Profile"
  | "Dashboard"
  | "Teacher"
  | "Subject"
  | "Student"
  | "Mark"
  | "ExamSession"
  | "Class"
  | "Guardian"
  | "Absence"
  | "Task"
  | "Message"
  | "Expense"
  | "Fee"
  | "StudentFee"
  | "Instance"
  | "Document"
  | "SettingsInstance"
  | "UserManagement"
  | "AuditLog"
  | "Staff"
  | "Leave"
  | "Payroll"
  | "AuthorizedPickUpPerson"
  | "StudentExit"
  | "CashBox"
  | "all";

export type AppAbilities = [Actions, Modules];

// Small helper to grant "everything" without using 'manage'
const ALL_ACTIONS: Exclude<Actions, "isSuperAdmin">[] = [
  "create",
  "read",
  "update",
  "delete",
  "getAll",
];

export const Permissions = {
  admin: {
    // one single element is enough; the builder below expands it to full CRUD+getAll on 'all'
    isSuperAdmin: ["all"],
  },

  // DIRECTOR (expanded, no 'manage')
  director: {
    create: ["Teacher", "Student", "Guardian", "Class", "Planner", "Message", "ExamSession", "Expense", "Fee", "UserManagement",],
    update: ["Teacher", "Student", "Guardian", "Class", "Planner", "ExamSession", "Expense", "Fee", "UserManagement"],
    delete: ["Teacher", "Student", "Guardian", "Class", "Planner", "ExamSession", "Expense", "Fee", "UserManagement",],
    getAll: ["Teacher", "Student", "Guardian", "Class", "Absence", "Planner", "Message", "ExamSession", "Expense", "Fee", "UserManagement", "AuditLog"],
    read: ["Dashboard", "Message", "SettingsInstance"],
  },

  "pedagogy-director": {
    create: ["Teacher", "Student", "Guardian", "Class", "Planner", "Message"],
    update: ["Teacher", "Student", "Guardian", "Class", "Planner"],
    delete: ["Teacher", "Student", "Guardian", "Class", "Planner", "ExamSession"],
    getAll: ["Teacher", "Student", "Guardian", "Class", "Absence", "Planner", "Message", "ExamSession"],
    read: ["Dashboard", "Message"],
  },

  manager: {
    create: ["Teacher", "Student", "Guardian", "Class", "Planner", "Message"],
    update: ["Teacher", "Student", "Guardian", "Class", "Planner"],
    delete: ["Teacher", "Student", "Guardian", "Class", "Planner", "ExamSession"],
    getAll: ["Teacher", "Student", "Guardian", "Class", "Absence", "Planner", "Message", "ExamSession"],
    read: ["Dashboard", "Message"],
  },

  // TEACHER (unchanged)
  teacher: {
    read: ["ExamSession", "Dashboard", "Message"],
    create: ["Absence", "Task", "Mark", "Message"],
  },

  // STUDENT (unchanged)
  student: {
    read: ["Routine", "Mark", "Absence", "Task", "Dashboard", "Message"],
    create: ["Message"],
  },

  // GUARDIAN (unchanged)
  guardian: {
    read: ["Routine", "Mark", "Absence", "Task", "Dashboard", "StudentFee", "Message"],
    create: ["Message"],
  },

  guest: {},
} as const;

export const defineAbilityFor = (
  role: keyof typeof Permissions
): Ability<AppAbilities> => {
  const { can, build } = new AbilityBuilder<Ability<AppAbilities>>(Ability);

  const rolePermissions = Permissions[role] || ({} as any);

  // Super admin shortcut: expand to full CRUD + getAll on 'all'
  if (Array.isArray(rolePermissions.isSuperAdmin) && rolePermissions.isSuperAdmin.includes("all")) {
    for (const action of ALL_ACTIONS) {
      can(action as AppAbilities[0], "all");
    }
    return build();
  }

  // Grant explicit permissions (no 'manage' anywhere)
  (["create", "read", "update", "delete", "getAll"] as const).forEach((action) => {
    const subjects = (rolePermissions as any)[action];
    if (Array.isArray(subjects)) {
      subjects.forEach((subject: string) => {
        can(action, subject as Modules);
      });
    }
  });

  return build();
};
